import type { FoodTemplate, MealItemHistoryEntry } from '@/api/types/nutrition'

/** カートに入れる食品（DB の検索結果と同じ形。shop_name は保存時に quantity になる） */
export type FoodCartItem = {
  food_name: string
  shop_name: string | null
  calories: number
  protein_g: number
  fat_g: number
  carb_g: number
}

/**
 * 履歴の食品をカートの形にする。
 * 保存時の quantity は shop_name 経由で渡るので、テンプレートと同じく量を入れる
 */
export function historyEntryToCartItem(entry: MealItemHistoryEntry): FoodCartItem & { scale: number } {
  return {
    food_name: entry.name,
    shop_name: entry.quantity,
    calories: entry.calories,
    protein_g: entry.protein_g,
    fat_g: entry.fat_g,
    carb_g: entry.carb_g,
    scale: entry.scale,
  }
}

type CartSource = 'db' | 'template' | 'history'

export type UnifiedCartItem = FoodCartItem & {
  source: CartSource
  /** 倍率と削除の単位。お気に入りは中の食品すべてで同じキーになる */
  key: string
  multiplier: number
  scale: number
  templateId?: number
  templateName?: string
}

export const templateCartKey = (id: number) => `template:${id}`
export const historyCartKey = (entry: MealItemHistoryEntry) => `history:${entry.item_id}`
export const dbCartKey = (item: FoodCartItem) => `db:${item.food_name}::${item.shop_name ?? ''}`

type BuildArgs = {
  templates: FoodTemplate[]
  selectedTemplateIds: Set<number>
  /** お気に入りの倍率（お気に入り管理画面と共有するため文字列で持つ） */
  templateMultipliers: Map<number, string>
  foodCart: FoodCartItem[]
  historyCart: MealItemHistoryEntry[]
  /** 履歴・DB の行ごとの倍率 */
  itemMultipliers: Map<string, number>
}

function scaled(base: FoodCartItem & { scale: number }, multiplier: number) {
  return {
    ...base,
    calories: base.calories * multiplier,
    protein_g: base.protein_g * multiplier,
    fat_g: base.fat_g * multiplier,
    carb_g: base.carb_g * multiplier,
    scale: base.scale * multiplier,
  }
}

export function buildUnifiedCart({
  templates,
  selectedTemplateIds,
  templateMultipliers,
  foodCart,
  historyCart,
  itemMultipliers,
}: BuildArgs): UnifiedCartItem[] {
  const items: UnifiedCartItem[] = []
  for (const id of selectedTemplateIds) {
    const t = templates.find((tpl) => tpl.id === id)
    if (!t) continue
    const multiplier = parseFloat(templateMultipliers.get(id) ?? '') || 1
    for (const item of t.items) {
      const base = {
        food_name: item.name,
        shop_name: item.quantity,
        calories: item.calories,
        protein_g: item.protein_g,
        fat_g: item.fat_g,
        carb_g: item.carb_g,
        scale: item.scale ?? 1,
      }
      items.push({
        ...scaled(base, multiplier),
        source: 'template',
        key: templateCartKey(id),
        multiplier,
        templateId: id,
        templateName: t.name,
      })
    }
  }
  for (const item of foodCart) {
    const key = dbCartKey(item)
    const multiplier = itemMultipliers.get(key) ?? 1
    items.push({ ...scaled({ ...item, scale: 1 }, multiplier), source: 'db', key, multiplier })
  }
  for (const entry of historyCart) {
    const key = historyCartKey(entry)
    const multiplier = itemMultipliers.get(key) ?? 1
    items.push({ ...scaled(historyEntryToCartItem(entry), multiplier), source: 'history', key, multiplier })
  }
  return items
}

export function cartToItems(cart: UnifiedCartItem[]) {
  return cart.map((item) => ({
    name: item.food_name,
    quantity: item.shop_name ? `${item.shop_name}` : null,
    calories: Math.round(item.calories),
    protein_g: Math.round(item.protein_g * 10) / 10,
    fat_g: Math.round(item.fat_g * 10) / 10,
    carb_g: Math.round(item.carb_g * 10) / 10,
    scale: item.scale,
    confidence: 'high' as const,
  }))
}

/** パレットで選んだもの（お気に入りか履歴。DB は DB タブで選ぶ） */
export type PalettePick =
  | { kind: 'template'; template: FoodTemplate }
  | { kind: 'history'; entry: MealItemHistoryEntry }

/** 選んだものを、記録する食品（倍率 1）にする */
export function pickToItems(pick: PalettePick) {
  return cartToItems(
    buildUnifiedCart({
      templates: pick.kind === 'template' ? [pick.template] : [],
      selectedTemplateIds: new Set(pick.kind === 'template' ? [pick.template.id] : []),
      templateMultipliers: new Map(),
      foodCart: [],
      historyCart: pick.kind === 'history' ? [pick.entry] : [],
      itemMultipliers: new Map(),
    }),
  )
}

/** 一覧に無ければ足し、あれば外す */
export function toggleByKey<T>(list: T[], item: T, keyOf: (x: T) => string): T[] {
  const key = keyOf(item)
  return list.some((x) => keyOf(x) === key) ? list.filter((x) => keyOf(x) !== key) : [...list, item]
}

type SubmitLabelArgs = {
  /** 表の ✏️ から開いた編集 */
  isEdit: boolean
  mealLabel: string
  cartCount: number
  hasAi: boolean
}

/** 記録ボタンの文言（押すと何が起きるかが分かるようにする） */
export function submitLabel({ isEdit, mealLabel, cartCount, hasAi }: SubmitLabelArgs): string {
  if (isEdit) return '保存'
  if (cartCount > 0 && hasAi) return `${mealLabel}に記録（${cartCount}品＋AI 解析）`
  if (cartCount > 0) return `${mealLabel}に記録（${cartCount}品）`
  if (hasAi) return `AI で解析して${mealLabel}に記録`
  return `${mealLabel}に記録`
}
